import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import './contact.css';

const ContactUs = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    subject: 'General Inquiry'
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
    document.title = "Contact Us | Construction Management System";
  }, []);

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const validateForm = () => {
    let errors = {};
    let isValid = true;

    if (!formData.name.trim()) {
      errors.name = "Name is required";
      isValid = false;
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email is invalid";
      isValid = false;
    }

    if (!formData.message.trim()) {
      errors.message = "Message is required";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      console.log("Form submitted:", formData);
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: '',
        subject: 'General Inquiry'
      });
      setFormErrors({});
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    }
  };

  const faqItems = [
    {
      question: "How can your construction management system help my business?",
      answer: "Our system helps you streamline project planning, resource allocation, budget tracking, and communication between teams. This leads to improved efficiency, reduced costs, and on-time project completion."
    },
    {
      question: "Is there a free trial available?",
      answer: "Yes, we offer a 14-day free trial with full access to all features. No credit card is required to start your trial."
    },
    {
      question: "Do you provide training for new users?",
      answer: "Absolutely! We provide comprehensive onboarding and training for all new clients. Our support team is available to help with setup and answer any questions you may have."
    },
    {
      question: "Can your system integrate with other software we use?",
      answer: "Our construction management system offers integration with popular accounting, scheduling, and project management tools. Contact us for specific integration requirements."
    }
  ];

  return (
    <div className="contact-us-container">
      {/* Contact Form and Info Section */}
      <motion.div 
        className="contact-main-container"
        initial="hidden"
        animate={isVisible ? "visible" : "hidden"}
        variants={staggerChildren}
      >
        <motion.div className="section-title" variants={fadeIn}>
          <h2>Contact Us</h2>
          <div className="underline"></div>
        </motion.div>
        
        <div className="contact-content">
          {/* Contact Form */}
          <motion.div 
            className="contact-form-container"
            variants={fadeIn}
            transition={{ delay: 0.2 }}
          >
            <h3>Send Us A Message</h3>

            {isSubmitted && (
              <motion.div 
                className="success-message"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                Thank you for your message! We'll get back to you shortly.
              </motion.div>
            )}
            
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Full Name<span className="required">*</span></label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={formErrors.name ? 'error' : ''}
                />
                {formErrors.name && <span className="error-message">{formErrors.name}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Email Address<span className="required">*</span></label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={formErrors.email ? 'error' : ''}
                />
                {formErrors.email && <span className="error-message">{formErrors.email}</span>}
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="company">Company Name</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="subject">Subject</label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                >
                  <option value="General Inquiry">General Inquiry</option>
                  <option value="Product Demo">Product Demo</option>
                  <option value="Support">Technical Support</option>
                  <option value="Pricing">Pricing Information</option>
                  <option value="Partnership">Partnership Opportunity</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="message">Message<span className="required">*</span></label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  className={formErrors.message ? 'error' : ''}
                ></textarea>
                {formErrors.message && <span className="error-message">{formErrors.message}</span>}
              </div>
              
              <motion.button 
                type="submit" 
                className="submit-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Send Message
              </motion.button>
            </form>
          </motion.div>
        </div>
      </motion.div>

      {/* Map Section */}
      <motion.div 
        className="map-section"
        initial="hidden"
        animate={isVisible ? "visible" : "hidden"}
        variants={fadeIn}
        transition={{ delay: 0.4 }}
      >
        <div className="section-title">
          <h2>Our Location</h2>
          <div className="underline"></div>
        </div>
        
        <div className="map-container">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d30263.078750908245!2d73.69520495!3d18.5912066!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2bbc048041295%3A0xd040be2db2f0eb9a!2sHinjawadi%2C%20Pimpri-Chinchwad%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1658481301047!5m2!1sen!2sin"
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Office Location"
          ></iframe>
        </div>
      </motion.div>

      {/* FAQ Section */}
      <motion.div 
        className="faq-section"
        initial="hidden"
        animate={isVisible ? "visible" : "hidden"}
        variants={staggerChildren}
        transition={{ delay: 0.6, staggerChildren: 0.2 }}
      >
        <motion.div className="section-title" variants={fadeIn}>
          <h2>Frequently Asked Questions</h2>
          <div className="underline"></div>
        </motion.div>
        
        <motion.div className="faq-container" variants={staggerChildren}>
          {faqItems.map((item, index) => (
            <motion.div 
              className="faq-item" 
              key={index}
              variants={fadeIn}
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <h3>{item.question}</h3>
              <p>{item.answer}</p>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* CTA Section */}
      <motion.div 
        className="cta-section"
        initial="hidden"
        animate={isVisible ? "visible" : "hidden"}
        variants={fadeIn}
        transition={{ delay: 0.8 }}
      >
        <div className="cta-content">
          <h2>Ready To Start Your Free Trial?</h2>
          <p>Experience how our construction management system can transform your business</p>
          <Link to="/register" className="cta-button-link">
            <motion.button 
              className="cta-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Start 14-Day Trial
            </motion.button>
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default ContactUs;